import { type FC, useMemo, useRef, useState, type JSX } from 'react';
import {
  Box,
  Button,
  CloseButton,
  Flex,
  HStack,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  Text,
  useMediaQuery,
} from '@chakra-ui/react';
import { first } from 'lodash';
import { DatePicker } from 'rsuite';
import moment from 'moment-timezone';
import { addMinutes, addSeconds, isBefore } from 'date-fns';
import { type TypeAttributes } from 'rsuite/esm/internals/types';
import cx from 'classnames';
import { ReactComponent as CalendarIcon } from '../../assets/svg/CalendarIcon.svg';
import { ReactComponent as ClockIcon } from '../../assets/svg/ClockIcon.svg';
import { ReactComponent as GoBack1hIcon } from '../../assets/svg/GoBack1hIcon.svg';
import { ReactComponent as GoBack2hIcon } from '../../assets/svg/GoBack2hIcon.svg';
import { ReactComponent as GoBack8hIcon } from '../../assets/svg/GoBack8hIcon.svg';
import { translate } from '../../constants/translation';
import { Timestamp } from '../../services/Timestamp';
import { Event, Page } from '../../types';
import { useAppInsights } from '../../hooks/useAppInsights';
import { getEventFactory } from '../../utils/get-event-factory';
import { useDetectMobileScreenWidth } from '../../hooks/useDetectMobileScreenWidth';
import { getLocalTimestampFromDate } from '../../utils/date-utils';

export interface Props {
  isOpen: boolean;
  operation: string;
  selectedVesselMmsi: string;
  onClose: () => void;
  timestamp: Timestamp;
  setTimestamp: (timestamp: Timestamp) => void;
  setStartTimestamp: (timestamp: Timestamp) => void;
  page: Page;
  timezone: string;
  isTripsPanelOpen: boolean;
}

const PickTimePopover: FC<Props> = ({
  isOpen,
  operation,
  selectedVesselMmsi,
  onClose,
  timestamp,
  setTimestamp,
  setStartTimestamp,
  page,
  timezone,
  isTripsPanelOpen,
}): JSX.Element => {
  const [selectedDate, setSelectedDate] = useState<Date>(null);
  const containerRef = useRef<HTMLElement>(undefined);
  const isMobileScreenWidth = useDetectMobileScreenWidth();
  const isLowResolutionDesktop = first(useMediaQuery('(max-height: 610px)'));
  const appInsights = useAppInsights();

  const handleClose = (): void => {
    resetInput();
    onClose();
  };

  const handleDateTimeChange = (date: Date): void => {
    if (!date) {
      setSelectedDate(null);
      return;
    }

    date = getCorrectDate(date);
    setSelectedDate(date);
  };

  const getLocalOperationDate = (date: Date): Date => {
    const utcDate = addMinutes(date, date.getTimezoneOffset());
    const offset = moment.tz.zone(timezone).utcOffset(date.valueOf());
    const localTimeOffset = Timestamp.fromMinutes(-offset);
    return addSeconds(utcDate, localTimeOffset.toSeconds());
  };

  const getCorrectDate = (date: Date): Date => {
    const operationCurrentTime = getLocalOperationDate(new Date());
    return isBefore(operationCurrentTime, date) ? operationCurrentTime : date;
  };

  const shouldDisableDate = (date: Date): boolean => {
    const midnightDate = new Date(date);
    midnightDate.setHours(0, 0, 0);
    const operationCurrentTime = getLocalOperationDate(new Date());

    return isBefore(operationCurrentTime, midnightDate);
  };

  const resetInput = (): void => {
    setSelectedDate(null);
  };

  const switchToPlayback = (timestamp: Timestamp): void => {
    const event = getEventFactory(Event.RedirectedToPlaybackFromPickTime)(
      page,
      operation,
      selectedVesselMmsi,
      timestamp
    );
    appInsights.trackAnalyticsEvent(event);
    setTimestamp(timestamp);
    setStartTimestamp(timestamp);
  };

  const handleGoClick = (): void => {
    const timestamp = getLocalTimestampFromDate(selectedDate, timezone);
    switchToPlayback(timestamp);
    handleClose();
  };

  const handleQuickReplayClick = (hoursAgo: number): void => {
    const timeToSubtract = Timestamp.fromHours(hoursAgo);

    switchToPlayback((timestamp || Timestamp.now()).subtract(timeToSubtract));
  };

  const renderCalendarIcon = (): JSX.Element => {
    return <CalendarIcon />;
  };

  const renderClockIcon = (): JSX.Element => {
    return <ClockIcon />;
  };

  const datePickerPlacement = useMemo<TypeAttributes.Placement>(() => {
    if (isMobileScreenWidth) {
      return 'topStart';
    }

    if (isLowResolutionDesktop) {
      return 'autoHorizontal';
    }

    return 'topStart';
  }, [isMobileScreenWidth, isLowResolutionDesktop]);

  const timePickerPlacement = useMemo<TypeAttributes.Placement>(() => {
    if (isMobileScreenWidth) {
      return 'top';
    }

    if (isLowResolutionDesktop) {
      return 'autoHorizontal';
    }

    return 'top';
  }, [isMobileScreenWidth, isLowResolutionDesktop]);

  const popoverClass = cx('pick-time', {
    'pick-time-liveview': page === Page.Liveview,
    'pick-time-playback': page === Page.Playback,
    expanded: isTripsPanelOpen,
  });

  return (
    <Popover isOpen={isOpen} isLazy>
      <PopoverContent className={popoverClass} ref={containerRef}>
        <PopoverBody>
          <Flex alignItems="flex-start" flexDirection="column">
            <CloseButton className="pick-time-close-button" onClick={handleClose} />
            <Text as="b" className="pick-time-panel-header">
              {translate('PICK_TIME')}
            </Text>
            <Text as="p" className="pick-time-panel-text" textAlign="left">
              {translate('PICK_TIME_DESCRIPTION')}
            </Text>
            <HStack spacing="12px" width="100%">
              <Box className="date-picker-container">
                <DatePicker
                  className="pick-time-date-picker"
                  menuClassName="pick-time-date-picker-menu"
                  format="yyyy-MM-dd"
                  placement={datePickerPlacement}
                  shouldDisableDate={shouldDisableDate}
                  editable
                  cleanable={false}
                  value={selectedDate}
                  caretAs={renderCalendarIcon}
                  onChange={handleDateTimeChange}
                  oneTap
                />
              </Box>
              <Box className="time-picker-container">
                <DatePicker
                  className="pick-time-date-picker"
                  menuClassName="pick-time-date-picker-menu"
                  format="HH:mm"
                  placeholder="00:00"
                  placement={timePickerPlacement}
                  editable
                  cleanable={false}
                  value={selectedDate}
                  caretAs={renderClockIcon}
                  onChange={handleDateTimeChange}
                />
              </Box>
              <Button className="go-button" variant="primary" isDisabled={!selectedDate} onClick={handleGoClick}>
                {translate('GO')}
              </Button>
            </HStack>
            <HStack width="100%" spacing="12px" marginTop="12px">
              <IconButton
                variant="secondary"
                icon={<GoBack1hIcon />}
                aria-label={translate('REPLAY_1H_AGO')}
                onClick={() => {
                  handleQuickReplayClick(1);
                }}
              />
              <IconButton
                variant="secondary"
                icon={<GoBack2hIcon />}
                aria-label={translate('REPLAY_2H_AGO')}
                onClick={() => {
                  handleQuickReplayClick(2);
                }}
              />
              <IconButton
                variant="secondary"
                icon={<GoBack8hIcon />}
                aria-label={translate('REPLAY_8H_AGO')}
                onClick={() => {
                  handleQuickReplayClick(8);
                }}
              />
            </HStack>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default PickTimePopover;
